/**
 * GeofenceDrawDialog — fullscreen Cesium map overlay for drawing a new
 * geofence (polygon or circle) directly on the map.
 *
 * Phase 8: replaces the typed-coordinate-only editor with an interactive
 * draw flow. Two modes:
 *
 *   - POLYGON: click to drop vertices, click "Finish" (or double-click)
 *     to close. The polygon's first vertex is repeated as the last so
 *     the GeoJSON is a valid closed ring.
 *
 *   - CIRCLE: click once to drop the center, click a second time to
 *     set the radius. We render a live preview circle while the user
 *     moves the cursor between the two clicks.
 *
 * On confirm, the dialog returns a normalized payload the parent
 * GeofencesPage forwards straight to the existing /api/v1/geofences
 * POST endpoint — circle = {centerLat, centerLng, radiusMeters},
 * polygon = {geometry: {type, coordinates}}.
 *
 * Kept self-contained: it uses its own Cesium Viewer (not the shared
 * one) so opening the dialog over a /geofences page doesn't disturb
 * the main /map viewer state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Viewer as ResiumViewer } from 'resium';
import type { CesiumComponentRef } from 'resium';
import {
  Viewer as CesiumViewer,
  Cartesian3,
  Cartographic,
  Color,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Math as CesiumMath,
  HeightReference,
  PolygonHierarchy,
  EllipsoidGeodesic,
  CallbackProperty,
  CustomDataSource,
} from 'cesium';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { applyBasemapToViewer } from '../map/basemaps';
import { useMapStore } from '../../store/mapStore';

export type DrawnGeofence =
  | {
      kind: 'polygon';
      // GeoJSON Polygon — outer ring only; closed (first == last).
      coordinates: number[][][];
    }
  | {
      kind: 'circle';
      centerLat: number;
      centerLng: number;
      radiusMeters: number;
    };

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (shape: DrawnGeofence) => void;
}

type Mode = 'POLYGON' | 'CIRCLE';

// Haversine via Cesium's geodesic — better than the flat-earth shortcut
// because we sometimes draw across a few miles of curvature.
function haversineMeters(a: Cartographic, b: Cartographic): number {
  const geo = new EllipsoidGeodesic(a, b);
  return geo.surfaceDistance;
}

export default function GeofenceDrawDialog({ open, onClose, onConfirm }: Props) {
  const basemapId = useMapStore((s) => s.basemapId);

  const viewerRef = useRef<CesiumComponentRef<CesiumViewer> | null>(null);
  const handlerRef = useRef<ScreenSpaceEventHandler | null>(null);

  const [mode, setMode] = useState<Mode>('POLYGON');

  // Polygon vertices. Each entry is [lng, lat] (GeoJSON ordering).
  const [polygonVertices, setPolygonVertices] = useState<[number, number][]>([]);

  // Circle state. `center` set on first click, `radiusMeters` set on
  // second click. `hoverPoint` tracks the cursor for the live preview.
  const [circleCenter, setCircleCenter] = useState<[number, number] | null>(null);
  const [circleRadius, setCircleRadius] = useState<number | null>(null);
  const [hoverPoint, setHoverPoint] = useState<[number, number] | null>(null);

  // Reset state when the dialog opens. Stale state from a prior open
  // would otherwise show old vertices on the map.
  useEffect(() => {
    if (open) {
      setMode('POLYGON');
      setPolygonVertices([]);
      setCircleCenter(null);
      setCircleRadius(null);
      setHoverPoint(null);
    }
  }, [open]);

  // Pull the user's GPS-zoomed view on first open so they don't start
  // staring at the prime meridian. Falls back silently if denied.
  useEffect(() => {
    if (!open || !viewerRef.current?.cesiumElement) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        viewerRef.current?.cesiumElement?.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            pos.coords.longitude,
            pos.coords.latitude,
            30_000,
          ),
          duration: 0.8,
        });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 4_000 },
    );
  }, [open]);

  // Apply basemap whenever it changes or the dialog opens.
  useEffect(() => {
    if (open && viewerRef.current?.cesiumElement) {
      applyBasemapToViewer(viewerRef.current.cesiumElement, basemapId);
    }
  }, [open, basemapId]);

  // Click + move event handlers. Tear down + rebuild whenever the active
  // mode changes so each mode can claim its own handler logic without
  // branching inside a single handler.
  const installHandlers = useCallback(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!viewer) return;
    handlerRef.current?.destroy();
    const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
    handlerRef.current = handler;

    const screenToLngLat = (
      position: { x: number; y: number },
    ): [number, number] | null => {
      const cart = viewer.camera.pickEllipsoid(
        position as unknown as Parameters<typeof viewer.camera.pickEllipsoid>[0],
        viewer.scene.globe.ellipsoid,
      );
      if (!cart) return null;
      const carto = Cartographic.fromCartesian(cart);
      return [CesiumMath.toDegrees(carto.longitude), CesiumMath.toDegrees(carto.latitude)];
    };

    handler.setInputAction((evt: { position: { x: number; y: number } }) => {
      const pt = screenToLngLat(evt.position);
      if (!pt) return;
      if (mode === 'POLYGON') {
        setPolygonVertices((v) => [...v, pt]);
      } else {
        // Two-click circle: first click sets center, second sets radius.
        // A third click resets and starts a new circle so the operator
        // can correct mistakes without leaving draw mode.
        setCircleCenter((current) => {
          if (current && circleRadius == null) {
            // Have center, need radius → use this click as radius.
            const centerCarto = Cartographic.fromDegrees(current[0], current[1]);
            const radiusCarto = Cartographic.fromDegrees(pt[0], pt[1]);
            setCircleRadius(haversineMeters(centerCarto, radiusCarto));
            return current;
          }
          // No center yet → use this click as center.
          setCircleRadius(null);
          return pt;
        });
      }
    }, ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction((evt: { endPosition: { x: number; y: number } }) => {
      const pt = screenToLngLat(evt.endPosition);
      if (pt) setHoverPoint(pt);
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }, [mode, circleRadius]);

  useEffect(() => {
    if (open) installHandlers();
    return () => {
      handlerRef.current?.destroy();
      handlerRef.current = null;
    };
  }, [open, installHandlers]);

  // Live preview vertex set for the polygon — vertices already laid down
  // PLUS the hovered cursor (so the user sees the rubber-band edge).
  const polygonPreviewVertices = useMemo<[number, number][]>(() => {
    if (mode !== 'POLYGON' || polygonVertices.length === 0) return polygonVertices;
    return hoverPoint ? [...polygonVertices, hoverPoint] : polygonVertices;
  }, [mode, polygonVertices, hoverPoint]);

  // Same trick for circle: while the user has a center but no radius
  // yet, render a preview circle whose radius tracks the cursor.
  const circlePreviewRadius = useMemo(() => {
    if (mode !== 'CIRCLE' || !circleCenter || circleRadius != null) return circleRadius;
    if (!hoverPoint) return null;
    return haversineMeters(
      Cartographic.fromDegrees(circleCenter[0], circleCenter[1]),
      Cartographic.fromDegrees(hoverPoint[0], hoverPoint[1]),
    );
  }, [mode, circleCenter, circleRadius, hoverPoint]);

  // CallbackProperty so the polygon redraws every frame against the
  // current cursor — way smoother than tearing down + recreating the
  // entity each mousemove.
  const polygonHierarchy = useMemo(
    () =>
      new CallbackProperty(() => {
        const ring = polygonPreviewVertices;
        if (ring.length < 2) return new PolygonHierarchy([]);
        return new PolygonHierarchy(
          ring.map(([lng, lat]) => Cartesian3.fromDegrees(lng, lat)),
        );
      }, false),
    [polygonPreviewVertices],
  );

  // Set up the entities once when the viewer is ready.
  useEffect(() => {
    const viewer = viewerRef.current?.cesiumElement;
    if (!open || !viewer) return;
    // Clear previous draw entities, then add the live ones.
    const dsName = '__geofence_draw__';
    let ds = viewer.dataSources.getByName(dsName)[0];
    if (!ds) {
      viewer.dataSources.add(new CustomDataSource(dsName));
      ds = viewer.dataSources.getByName(dsName)[0];
    }
    ds.entities.removeAll();

    if (mode === 'POLYGON' && polygonPreviewVertices.length >= 2) {
      ds.entities.add({
        polygon: {
          hierarchy: polygonHierarchy,
          material: Color.fromCssColorString('#ffb547').withAlpha(0.35),
          outline: true,
          outlineColor: Color.fromCssColorString('#ffb547'),
          // outlineWidth often ignored by Cesium's polygon rasterizer; the
          // polyline overlay below ensures a visible edge regardless.
          heightReference: HeightReference.CLAMP_TO_GROUND,
        },
        polyline: {
          positions: new CallbackProperty(
            () =>
              polygonPreviewVertices.map(([lng, lat]) =>
                Cartesian3.fromDegrees(lng, lat),
              ),
            false,
          ),
          width: 2,
          material: Color.fromCssColorString('#ffb547'),
          clampToGround: true,
        },
      });
    }

    if (mode === 'CIRCLE' && circleCenter) {
      const effectiveRadius = circlePreviewRadius ?? 50;
      ds.entities.add({
        position: Cartesian3.fromDegrees(circleCenter[0], circleCenter[1]),
        ellipse: {
          semiMajorAxis: effectiveRadius,
          semiMinorAxis: effectiveRadius,
          material: Color.fromCssColorString('#ffb547').withAlpha(0.35),
          outline: true,
          outlineColor: Color.fromCssColorString('#ffb547'),
          heightReference: HeightReference.CLAMP_TO_GROUND,
        },
        point: {
          pixelSize: 6,
          color: Color.fromCssColorString('#ffb547'),
          outlineColor: Color.BLACK,
          outlineWidth: 1,
          heightReference: HeightReference.CLAMP_TO_GROUND,
        },
      });
    }
  }, [
    open,
    mode,
    polygonHierarchy,
    polygonPreviewVertices,
    circleCenter,
    circlePreviewRadius,
  ]);

  const canConfirm =
    (mode === 'POLYGON' && polygonVertices.length >= 3) ||
    (mode === 'CIRCLE' && circleCenter != null && circleRadius != null && circleRadius > 5);

  const handleConfirm = () => {
    if (mode === 'POLYGON') {
      // Close the ring: GeoJSON requires the first vertex repeated at end.
      const ring = [...polygonVertices, polygonVertices[0]];
      onConfirm({
        kind: 'polygon',
        coordinates: [ring.map(([lng, lat]) => [lng, lat])],
      });
    } else if (circleCenter && circleRadius != null) {
      onConfirm({
        kind: 'circle',
        centerLat: circleCenter[1],
        centerLng: circleCenter[0],
        radiusMeters: circleRadius,
      });
    }
  };

  const handleUndo = () => {
    if (mode === 'POLYGON') {
      setPolygonVertices((v) => v.slice(0, -1));
    } else {
      // Step back: if radius set, drop just the radius; else clear center.
      if (circleRadius != null) setCircleRadius(null);
      else setCircleCenter(null);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-iron-950/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-200"
            enterFrom="opacity-0 scale-[0.98]"
            enterTo="opacity-100 scale-100"
            leave="transform transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-[0.98]"
          >
            <Dialog.Panel className="m-4 flex-1 bg-iron-900 border border-iron-600 rounded-md flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 h-12 border-b border-iron-700 flex-shrink-0">
                <Dialog.Title className="text-sm font-semibold text-iron-100 flex items-center gap-3">
                  Draw Geofence
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-iron-400">
                    {mode === 'POLYGON'
                      ? `${polygonVertices.length} vertices`
                      : circleCenter
                        ? circleRadius != null
                          ? `${Math.round(circleRadius)} m radius`
                          : 'click to set radius'
                        : 'click to set center'}
                  </span>
                </Dialog.Title>

                {/* Mode toggle */}
                <div className="inline-flex bg-iron-800 border border-iron-600 rounded-sm p-0.5">
                  {(['POLYGON', 'CIRCLE'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        setMode(m);
                        setPolygonVertices([]);
                        setCircleCenter(null);
                        setCircleRadius(null);
                      }}
                      className={clsx(
                        'px-3 py-1 text-[11px] font-mono font-semibold uppercase tracking-[0.15em] rounded-sm transition-colors',
                        mode === m
                          ? 'bg-accent text-iron-900'
                          : 'text-iron-300 hover:text-iron-100',
                      )}
                    >
                      {m}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 text-iron-400 hover:text-iron-100"
                  title="Cancel"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Map */}
              <div className="flex-1 relative">
                <ResiumViewer
                  full
                  ref={viewerRef}
                  animation={false}
                  timeline={false}
                  homeButton={false}
                  baseLayerPicker={false}
                  infoBox={false}
                  navigationHelpButton={false}
                  sceneModePicker={false}
                  selectionIndicator={false}
                  shouldAnimate={false}
                />

                {/* Instructions overlay — top-left */}
                <div className="absolute top-3 left-3 px-3 py-2 bg-iron-900/90 border border-iron-600 rounded-md text-[11px] text-iron-300 max-w-xs">
                  {mode === 'POLYGON' ? (
                    <>
                      <div className="font-semibold text-iron-100 mb-1">Polygon mode</div>
                      <div>Click to drop vertices. Need at least 3 to save.</div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold text-iron-100 mb-1">Circle mode</div>
                      <div>Click to set the center, click again to set the radius.</div>
                    </>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 h-12 border-t border-iron-700 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleUndo}
                  className="text-[12px] text-iron-300 hover:text-iron-100"
                >
                  Undo last
                </button>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={onClose} className="btn-secondary">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                    className="btn-primary disabled:opacity-50"
                  >
                    Use this shape
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
